/**
 * Блок «Доходы и занятость» (ТЗ п. 5.2.10, правила R-17, R-18, R-25…R-28).
 */

import { newRowId, type OwnedOrganization } from '@bd/domain';
import { DataGrid } from '../../../components/DataGrid.js';
import { CheckboxField, InnField, MoneyField, TextField } from '../../../components/fields/index.js';
import type { BlockProps } from './types.js';

export function IncomeBlock({ profile, update, readOnly, errorFor }: BlockProps) {
  const income = profile.income;
  const set = (patch: Partial<typeof income>) => update((p) => ({ ...p, income: { ...p.income, ...patch } }));

  return (
    <section className="block">
      <h2>Доходы и занятость</h2>

      <CheckboxField label="Должник трудоустроен" checked={income.employed} readOnly={readOnly} onChange={(v) => set({ employed: v })} />

      {income.employed && (
        <>
          <div className="row row-2">
            <TextField
              label="Наименование работодателя"
              value={income.employerName}
              required
              readOnly={readOnly}
              error={errorFor('income.employerName')}
              onChange={(v) => set({ employerName: v })}
            />
            <InnField
              label="ИНН работодателя"
              value={income.employerInn}
              readOnly={readOnly}
              onChange={(v) => set({ employerInn: v })}
              onResolved={(party) => set({ employerName: party.name })}
            />
          </div>

          <div className="row row-2">
            <MoneyField
              label="Среднемесячная заработная плата"
              kopecks={income.monthlySalary}
              readOnly={readOnly}
              error={errorFor('income.monthlySalary')}
              onChange={(v) => set({ monthlySalary: v })}
            />
            <TextField
              label="Подтверждающий документ"
              value={income.salaryConfirmation}
              readOnly={readOnly}
              hint="Например: справка по форме 2-НДФЛ"
              onChange={(v) => set({ salaryConfirmation: v })}
            />
          </div>
        </>
      )}

      <CheckboxField
        label="Получает пособия и выплаты СФР"
        checked={income.hasSocialPayments}
        readOnly={readOnly}
        onChange={(v) => set({ hasSocialPayments: v })}
      />
      {income.hasSocialPayments && (
        <TextField
          label="Какие выплаты получает"
          value={income.socialPaymentsDescription}
          required
          readOnly={readOnly}
          error={errorFor('income.socialPaymentsDescription')}
          hint="Текст подставляется в заявление дословно"
          onChange={(v) => set({ socialPaymentsDescription: v })}
        />
      )}

      <CheckboxField
        label="Имеет статус индивидуального предпринимателя"
        checked={income.isEntrepreneur}
        readOnly={readOnly}
        onChange={(v) => set({ isEntrepreneur: v })}
      />
      {income.isEntrepreneur && (
        <TextField label="ОГРНИП" value={income.entrepreneurOgrnip ?? ''} readOnly={readOnly} onChange={(v) => set({ entrepreneurOgrnip: v })} />
      )}

      <CheckboxField
        label="Является участником (акционером) юридических лиц"
        checked={income.hasOrganizations}
        readOnly={readOnly}
        onChange={(v) => set({ hasOrganizations: v })}
      />

      {income.hasOrganizations && (
        <DataGrid<OwnedOrganization>
          rows={income.organizations}
          readOnly={readOnly}
          getRowId={(row) => row.id}
          onChange={(apply) => update((p) => ({ ...p, income: { ...p.income, organizations: apply(p.income.organizations) } }))}
          createRow={() => ({ id: newRowId(), name: '', inn: '', ogrn: '', address: '', share: '' })}
          columns={[
            { key: 'name', title: 'Наименование', width: 28, get: (r) => r.name, set: (r, v) => ({ ...r, name: v }) },
            { key: 'inn', title: 'ИНН', width: 14, get: (r) => r.inn, set: (r, v) => ({ ...r, inn: v }) },
            { key: 'ogrn', title: 'ОГРН', width: 16, get: (r) => r.ogrn, set: (r, v) => ({ ...r, ogrn: v }) },
            { key: 'address', title: 'Адрес', width: 28, get: (r) => r.address, set: (r, v) => ({ ...r, address: v }) },
            { key: 'share', title: 'Доля', width: 14, get: (r) => r.share, set: (r, v) => ({ ...r, share: v }) },
          ]}
        />
      )}
    </section>
  );
}
